/**
 * sl-9fmv.6.5: PendingPatchRegistry
 *
 * Workspace-scoped state store for AI-authored workflow patches that are
 * awaiting user decision (accept / reject). Patches arrive via the
 * `automation:workflow-patch` Socket.IO event and are routed into the
 * registry by AutomationSocketService.
 *
 * The registry is a pure state container — it owns no transport, no
 * timers, no business logic beyond lifecycle transitions. UI consumers
 * subscribe via the LiveData `patches$` stream and render a diff-preview
 * layer (sl-9fmv.6.6) plus accept/reject controls (sl-9fmv.6.7).
 *
 * Lifecycle:
 *   pending → accepted  (user confirmed, commit snapshot)
 *   pending → rejected  (user discarded, revert snapshot)
 *   pending → stale     (newer patch or manual user edit superseded)
 *
 * Accepted / rejected / stale patches stay in the map so late-arriving
 * subscribers still see the final status; the caller decides when to
 * forget (sl-9fmv.6.7 clears accepted patches on chat-session reset).
 */
import type {
  PatchStatus,
  WorkflowPatchEvent,
} from '@streamlain/workflow-field-schemas';
import { Entity, LiveData } from '@toeverything/infra';

export class PendingPatchRegistry extends Entity {
  readonly patches$ = new LiveData<ReadonlyMap<string, WorkflowPatchEvent>>(
    new Map()
  );

  /**
   * Register a freshly-arrived pending patch. Idempotent on `patchId` —
   * a duplicate event (network retry, reconnect replay) is ignored so the
   * UI does not flicker or double-count.
   */
  addPending(event: WorkflowPatchEvent): void {
    if (event.status !== 'pending') return;
    const current = this.patches$.value;
    if (current.has(event.patchId)) return;
    const next = new Map(current);
    next.set(event.patchId, event);
    this.patches$.next(next);
  }

  markAccepted(patchId: string): void {
    this.transition(patchId, 'accepted');
  }

  markRejected(patchId: string): void {
    this.transition(patchId, 'rejected');
  }

  markStale(patchId: string): void {
    this.transition(patchId, 'stale');
  }

  /**
   * Mark every pending patch for a given workflow as stale. Called by the
   * editor when the user makes a manual edit while patches are awaiting
   * decision — the proposed diff no longer matches the current state so
   * accepting it would clobber the user's work.
   */
  markWorkflowStale(workflowId: string): void {
    const current = this.patches$.value;
    let mutated: Map<string, WorkflowPatchEvent> | null = null;
    for (const [id, event] of current) {
      if (event.workflowId !== workflowId) continue;
      if (event.status !== 'pending') continue;
      if (!mutated) mutated = new Map(current);
      mutated.set(id, { ...event, status: 'stale' });
    }
    if (mutated) this.patches$.next(mutated);
  }

  getPending(workflowId: string): WorkflowPatchEvent[] {
    const result: WorkflowPatchEvent[] = [];
    for (const event of this.patches$.value.values()) {
      if (event.workflowId !== workflowId) continue;
      if (event.status !== 'pending') continue;
      result.push(event);
    }
    return result;
  }

  getPatchById(patchId: string): WorkflowPatchEvent | undefined {
    return this.patches$.value.get(patchId);
  }

  private transition(patchId: string, next: PatchStatus): void {
    const current = this.patches$.value;
    const existing = current.get(patchId);
    if (!existing) return;
    if (existing.status === next) return;
    const updated = new Map(current);
    updated.set(patchId, { ...existing, status: next });
    this.patches$.next(updated);
  }
}

/**
 * Pure routing function — the socket handler delegates here so the
 * decision logic is trivially unit-testable without instantiating the
 * socket service.
 */
export function routeWorkflowPatchEvent(
  registry: PendingPatchRegistry,
  event: WorkflowPatchEvent
): void {
  switch (event.status) {
    case 'pending':
      registry.addPending(event);
      break;
    case 'accepted':
      registry.markAccepted(event.patchId);
      break;
    case 'rejected':
      registry.markRejected(event.patchId);
      break;
    case 'stale':
      registry.markStale(event.patchId);
      break;
  }
}
